/**
 * Blocking a number by hand.
 *
 * The row menu on a call is the normal way in - somebody answers a robot and
 * blocks it while it is still on screen. This exists for the case the row menu
 * cannot serve: a number that has not got through yet. A desk that has been
 * warned about a caller, or has just watched the same number ring three
 * colleagues, should not have to wait to be rung again before it can act.
 *
 * Everything typed here is `manual` rather than `spam`, and the difference is
 * what the entry can be trusted to mean afterwards. `spam` says a person heard
 * this caller and judged it; `manual` says somebody typed it in. Six months
 * later, when a customer says they cannot get through, that distinction is the
 * whole story.
 */
import { useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { addInboundBlock, type InboundBlock } from "../../../../lib/callBlocklist";
import { normalizeE164 } from "../../../../lib/phone";
import { Btn, Modal } from "../../../ui";

export function AddBlockModal({
  onClose,
  onAdded,
  onError,
}: {
  onClose: () => void;
  onAdded: (row: InboundBlock) => void;
  onError: () => void;
}) {
  const t = useT();
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Checked here as well as on the server, and not to save the request: a
  // number that cannot be canonicalised would be stored as typed and then match
  // nothing that ever arrives - an entry sitting on the list blocking nobody.
  // The operator finds out while the number is still in front of them.
  const normalized = normalizeE164(phone);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!normalized) return;
    setBusy(true);
    try {
      const res = await addInboundBlock({
        phone: normalized,
        source: "manual",
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onAdded(res.block);
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("calls.blocklist.add")}
      label={t("calls.blocklist.add")}
      onClose={onClose}
      className="max-w-[460px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("calls.blocklist.col.number")} *</span>
          <label className="crm-inp">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("calls.blocklist.numberPlaceholder")}
              inputMode="tel"
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
            />
          </label>
          {/* Only once there is something to judge. A red line under an empty
              field is the form telling somebody off for not having typed yet. */}
          {phone.trim() && !normalized ? (
            <span className="mt-1 block text-[11.5px] text-destructive">{t("calls.blocklist.badNumber")}</span>
          ) : null}
        </div>

        <div>
          <span className="crm-lab">{t("calls.blocklist.col.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("calls.blocklist.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        {/* What actually happens to the caller, in one sentence. Nobody guesses
            this right: a blocked call is refused before it is answered, so
            there is no ring, no announcement and no missed-call row - which is
            also why blocking the wrong number is invisible until somebody
            complains through another channel. */}
        <p className="crm-sub">{t("calls.blocklist.addManualHint")}</p>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || !normalized}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4" />}
            {t("calls.blocklist.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
